// lib/print-export-client.ts
import type { PrintSnapshot } from '#types/types';

// The worker imports print-export codecs; importing its launcher there makes Turbopack recurse.
const createWorker = (): Worker => new Worker(new URL('../workers/print-export.worker.ts', import.meta.url), { type: 'module' });
let activePrintWorker: Worker | null = null;

export const runPrintExport = async ({
  snapshot,
  signal,
  onProgress,
}: {
  snapshot: PrintSnapshot;
  signal?: AbortSignal;
  onProgress?: (percent: number) => void;
}): Promise<{ file: File; extension: 'tif' | 'jpg' }> => {
  if (signal?.aborted) throw new DOMException('Print export aborted.', 'AbortError');
  if (activePrintWorker) throw new Error('PRINT_EXPORT_ALREADY_RUNNING');
  const worker = createWorker();
  activePrintWorker = worker;
  const jobId = `print-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const transfer: Transferable[] = [snapshot.icc];
  snapshot.layers.forEach((layer) => {
    if (layer.source.kind === 'arrayBuffer') transfer.push(layer.source.value as ArrayBuffer);
  });
  return new Promise((resolve, reject) => {
    let settled = false;
    let lastProgress = 0;
    const cleanup = () => {
      worker.onmessage = null;
      worker.onerror = null;
      signal?.removeEventListener('abort', abort);
      worker.terminate();
      if (activePrintWorker === worker) activePrintWorker = null;
    };
    const abort = () => {
      if (settled) return;
      settled = true;
      worker.postMessage({ type: 'abort', jobId });
      cleanup();
      reject(new DOMException('Print export aborted.', 'AbortError'));
    };
    signal?.addEventListener('abort', abort, { once: true });
    worker.onerror = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('PRINT_WORKER_FAILED'));
    };
    worker.onmessage = (event: MessageEvent<{ type: string; jobId: string; percent?: number; bytes?: ArrayBuffer; mime?: string; extension?: 'tif' | 'jpg'; code?: string; message?: string }>) => {
      const message = event.data;
      if (message.jobId !== jobId || settled) return;
      if (message.type === 'progress') {
        lastProgress = Math.max(lastProgress, Math.max(0, Math.min(100, Number(message.percent) || 0)));
        onProgress?.(lastProgress);
        return;
      }
      if (message.type === 'error') {
        settled = true; cleanup(); reject(new Error(`${message.code || 'PRINT_WORKER_FAILED'}${message.message ? `: ${message.message}` : ''}`)); return;
      }
      if (message.type === 'done' && message.bytes && message.mime && message.extension) {
        settled = true; cleanup(); onProgress?.(100);
        resolve({ file: new File([message.bytes], `background.${message.extension}`, { type: message.mime }), extension: message.extension });
      }
    };
    worker.postMessage({ type: 'start', jobId, snapshot }, transfer);
  });
};
